import { useRef, useState } from 'react';

interface SharedProps {
  /** `<input accept>` value, e.g. `.csv,.xlsx` or `.jpg,.jpeg,.png,.webp,.gif,.pdf`. */
  accept: string;
  placeholder?: string;
}

interface SingleFileProps extends SharedProps {
  multiple?: false;
  file: File | null;
  onFileSelected: (file: File | null) => void;
}

interface MultiFileProps extends SharedProps {
  multiple: true;
  files: File[];
  onFilesSelected: (files: File[]) => void;
  /** Caps how many files a drop/browse selection can add in total. */
  maxFiles?: number;
}

export type FileDropzoneProps = SingleFileProps | MultiFileProps;

/**
 * The one file-upload control for the whole app: drag-and-drop or click to
 * browse, same `.ne-dropzone` look everywhere a file is picked (originally
 * `ImportModal`'s own inline markup - pulled out here so every other upload
 * flow matches it instead of inventing its own input). Reuse this for any
 * new upload control rather than writing a bare `<input type="file">`.
 *
 * Single-file mode (`file`/`onFileSelected`, the default) is unchanged from
 * before. `multiple` mode (`files`/`onFilesSelected`) accumulates newly
 * picked/dropped files onto the existing list (capped at `maxFiles`) and
 * renders each with its own remove button, for upload flows that take
 * several files in one go (e.g. a multi-photo quotation upload).
 */
export function FileDropzone(props: FileDropzoneProps) {
  const { accept, placeholder } = props;
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function addFiles(incoming: FileList | File[]) {
    const picked = Array.from(incoming);
    if (picked.length === 0) return;
    if (props.multiple) {
      const next = [...props.files, ...picked].slice(0, props.maxFiles ?? Infinity);
      props.onFilesSelected(next);
    } else {
      props.onFileSelected(picked[0] ?? null);
    }
  }

  const hasSelection = props.multiple ? props.files.length > 0 : Boolean(props.file);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <div
        className={dragActive ? 'ne-dropzone ne-dropzone--active' : 'ne-dropzone'}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={props.multiple}
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {!hasSelection ? (
          <span>{placeholder ?? 'Drag and drop a file here, or click to browse.'}</span>
        ) : props.multiple ? (
          <span>
            {props.files.length} file{props.files.length === 1 ? '' : 's'} selected · click or drop
            to add more
          </span>
        ) : (
          <span>
            {props.file!.name} · {(props.file!.size / 1024).toFixed(0)} KB
          </span>
        )}
      </div>
      {props.multiple && props.files.length > 0 ? (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 2 }}>
          {props.files.map((f, i) => (
            <li
              key={`${f.name}-${f.size}-${i}`}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <span>
                {f.name} · {(f.size / 1024).toFixed(0)} KB
              </span>
              <button
                type="button"
                onClick={() => props.onFilesSelected(props.files.filter((_, j) => j !== i))}
                aria-label={`Remove ${f.name}`}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                }}
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
